/**
 * Clock arithmetic for the Schedules surface (task 1003).
 *
 * The next-three-runs preview is the confirmation step of the create form: a
 * User cannot read `0 9 * * 1-5` and know it means weekday mornings, and a
 * preview computed differently from the clock that fires would be worse than
 * none. So this module uses `cron-parser` at the same major version
 * `innovate-service/src/lib/schedules/cron.ts` uses (which is BullMQ's own cron
 * library), and reads a five-field expression the same way that module does:
 *
 *   FIVE FIELDS ONLY. cron-parser accepts a six-field expression whose leading
 *   field is seconds; the service refuses one, so accepting it here would show
 *   a preview for a rule that cannot be saved.
 *
 * Everything here is pure. Nothing decides whether a rule is ALLOWED — the
 * plan's `min_schedule_interval_seconds` floor is the service's answer, arriving
 * as a `402 limit_exceeded` the EntitlementGuard renders.
 */
import { parseExpression } from 'cron-parser';

/** How many occurrences the save-time preview shows. */
export const PREVIEW_OCCURRENCE_COUNT = 3;

export interface CronValidationResult {
  valid: boolean;
  /** Human-readable reason, present only when `valid` is false. */
  error?: string;
}

/** Exactly five whitespace-separated fields? */
const hasFiveFields = (expression: string): boolean =>
  expression.trim().split(/\s+/).length === 5;

/**
 * Is this an IANA timezone this browser knows? `Intl` is the authority the cron
 * library resolves against, so asking it directly keeps the refusal in the form
 * rather than at save time.
 */
export const isValidTimezone = (timezone: string): boolean => {
  try {
    // Throws RangeError for an unknown zone.
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/** The viewer's own zone — the default a new schedule opens on. */
export const browserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** Validate a five-field cron expression in a timezone, with the reason. */
export const validateCron = (cron: string, timezone: string): CronValidationResult => {
  if (!cron.trim()) {
    return { valid: false, error: 'Enter a cron expression' };
  }
  if (!hasFiveFields(cron)) {
    return {
      valid: false,
      error: 'A cron expression has exactly five fields: minute hour day-of-month month day-of-week',
    };
  }
  if (!isValidTimezone(timezone)) {
    return { valid: false, error: `'${timezone}' is not a known IANA timezone` };
  }
  try {
    parseExpression(cron, { tz: timezone });
    return { valid: true };
  } catch (error) {
    return { valid: false, error: (error as Error).message };
  }
};

/**
 * The next `count` instants this cron produces, strictly after `from`.
 *
 * An expression this module refuses has no occurrences by definition, so the
 * answer is an empty list rather than a throw — the form renders the validation
 * message instead of a preview.
 */
export const nextOccurrences = (
  cron: string,
  timezone: string,
  count: number = PREVIEW_OCCURRENCE_COUNT,
  from: Date = new Date()
): Date[] => {
  if (count <= 0 || !validateCron(cron, timezone).valid) return [];

  try {
    const interval = parseExpression(cron, { tz: timezone, currentDate: from });
    const occurrences: Date[] = [];
    for (let index = 0; index < count; index += 1) {
      occurrences.push(interval.next().toDate());
    }
    return occurrences;
  } catch {
    // cron-parser throws once an expression runs out of occurrences; what it
    // produced before that is still the truthful answer, and an empty list is
    // the truthful answer when it produced nothing.
    return [];
  }
};

/**
 * The rule in words, for the list's "When" column.
 *
 * Deliberately a small table of the shapes the preset chips produce plus a few
 * near neighbours, falling back to the raw expression. A general cron-to-English
 * translator is a library-sized problem, and the row shows the raw expression
 * underneath either way — so the sentence never has to carry the whole meaning.
 */
export const describeCron = (cron: string): string => {
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) return cron;
  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;

  const everyField = (value: string) => value === '*';
  const fixed = (value: string) => /^\d+$/.test(value);
  const timeOfDay = () =>
    fixed(hour) && fixed(minute)
      ? formatClock(Number(hour), Number(minute))
      : `${hour}:${minute}`;

  if (everyField(minute) && everyField(hour)) return 'Every minute';
  if (fixed(minute) && everyField(hour) && everyField(dayOfMonth) && everyField(month) && everyField(dayOfWeek)) {
    return minute === '0' ? 'Every hour' : `Every hour at ${minute} past`;
  }
  if (/^\*\/\d+$/.test(minute) && everyField(hour)) {
    return `Every ${minute.slice(2)} minutes`;
  }
  if (fixed(minute) && fixed(hour) && everyField(dayOfMonth) && everyField(month)) {
    if (everyField(dayOfWeek)) return `Every day at ${timeOfDay()}`;
    if (dayOfWeek === '1-5') return `Weekdays at ${timeOfDay()}`;
    if (fixed(dayOfWeek)) return `Every ${WEEKDAY_NAMES[Number(dayOfWeek) % 7]} at ${timeOfDay()}`;
  }
  if (fixed(minute) && fixed(hour) && fixed(dayOfMonth) && everyField(month) && everyField(dayOfWeek)) {
    return `Monthly on the ${ordinal(Number(dayOfMonth))} at ${timeOfDay()}`;
  }
  return cron;
};

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const ordinal = (value: number): string => {
  const remainderTen = value % 10;
  const remainderHundred = value % 100;
  if (remainderTen === 1 && remainderHundred !== 11) return `${value}st`;
  if (remainderTen === 2 && remainderHundred !== 12) return `${value}nd`;
  if (remainderTen === 3 && remainderHundred !== 13) return `${value}rd`;
  return `${value}th`;
};

/** 9:00 AM, from the cron's own 24-hour fields. */
const formatClock = (hour: number, minute: number): string => {
  const suffix = hour < 12 ? 'AM' : 'PM';
  const twelveHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelveHour}:${String(minute).padStart(2, '0')} ${suffix}`;
};

/** An instant as the given zone reads it: "Tue Sep 22, 2026 · 9:00 AM CDT". */
export const formatInTimezone = (instant: Date, timezone: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: timezone,
    }).format(instant);
  } catch {
    return instant.toISOString();
  }
};

/**
 * The plan's interval floor in words — "once every hour", "once every 5
 * minutes" — for the sentence under the preview. The number itself is the
 * account's resolved `min_schedule_interval_seconds`; nothing is assumed when
 * the limit has not resolved, and the caller renders no sentence at all.
 */
export const describeInterval = (seconds: number): string => {
  if (seconds % 86_400 === 0) {
    const days = seconds / 86_400;
    return days === 1 ? 'once a day' : `once every ${days} days`;
  }
  if (seconds % 3_600 === 0) {
    const hours = seconds / 3_600;
    return hours === 1 ? 'once an hour' : `once every ${hours} hours`;
  }
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return minutes === 1 ? 'once a minute' : `once every ${minutes} minutes`;
  }
  return `once every ${seconds} seconds`;
};

/**
 * How far ahead of UTC a zone is at a given instant, in milliseconds.
 *
 * Read from `Intl` rather than from a table: the browser already carries the
 * IANA database, and a second copy of the DST rules would be a second answer to
 * a question that must have one.
 */
const zoneOffsetMs = (instant: Date, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const field = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    field('year'),
    field('month') - 1,
    field('day'),
    field('hour') % 24,
    field('minute'),
    field('second')
  );
  return asUtc - instant.getTime();
};

/**
 * The instant a wall-clock date and time name in a given zone.
 *
 * `date` is `YYYY-MM-DD` and `time` is `HH:MM`, exactly what the form's date and
 * time inputs produce. The offset is resolved twice because the first guess can
 * land on the wrong side of a daylight-saving change, and the second pass uses
 * the offset in force at the instant the first pass found.
 */
export const wallClockToInstant = (date: string, time: string, timeZone: string): Date | null => {
  if (!date || !time) return null;
  const naive = new Date(`${date}T${time}:00Z`);
  if (Number.isNaN(naive.getTime()) || !isValidTimezone(timeZone)) return null;
  const firstPass = new Date(naive.getTime() - zoneOffsetMs(naive, timeZone));
  return new Date(naive.getTime() - zoneOffsetMs(firstPass, timeZone));
};

/** The `YYYY-MM-DD` and `HH:MM` an instant reads as in a zone — the edit case. */
export const instantToWallClock = (
  instant: Date,
  timeZone: string
): { date: string; time: string } => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant);
  const field = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  const hour = field('hour') === '24' ? '00' : field('hour');
  return {
    date: `${field('year')}-${field('month')}-${field('day')}`,
    time: `${hour}:${field('minute')}`,
  };
};

/**
 * A cron expression from the form's date/time parts, in the shape the preset
 * chips produce. `dayOfWeek` and `dayOfMonth` carry the preset's own rule.
 */
export const buildCron = (parts: {
  minute: number;
  hour: number;
  dayOfMonth?: string;
  dayOfWeek?: string;
}): string =>
  [
    String(parts.minute),
    String(parts.hour),
    parts.dayOfMonth ?? '*',
    '*',
    parts.dayOfWeek ?? '*',
  ].join(' ');
